import React, { useRef } from 'react';
import { Box, InputAdornment } from '@mui/material';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import { CustomDateRangePickerProps } from '../../helpers/types';
import { useEnterAppliesDateRange } from '../../hooks/useEnterAppliesDateRange';
import {
  StyledArrow,
  StyledDateRangePicker,
  StyledTextField,
} from './Style/CustomDateRangePicker.style';

const CustomDateRangePicker = ({
  onChange,
  defaultStartDate,
  defaultEndDate,
  disableDate,
  placement = 'bottomStart',
}: CustomDateRangePickerProps) => {
  const startDate: any = defaultStartDate ? new Date(defaultStartDate) : null;
  const endDate: any = defaultEndDate ? new Date(defaultEndDate) : null;

  const datePickerRef = useRef<any>(null);
  const { handleOpen, handleClose } = useEnterAppliesDateRange(datePickerRef);

  const formatToCustomDate = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
    };
    return date ? date.toLocaleDateString('en-GB', options) : '';
  };

  // Format date as YYYY-MM-DD (no time component) for API compatibility
  const formatDateOnly = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (value: any) => {
    if (value && value.length === 2) {
      const [start, end] = value;

      const startDateObj = new Date(start);
      const endDateObj = new Date(end);

      onChange([formatDateOnly(startDateObj), formatDateOnly(endDateObj)]);
    }
  };

  const handleTextFieldClick = () => {
    datePickerRef.current?.open();
  };

  const formattedStartDate = startDate ? formatToCustomDate(startDate) : '';
  const formattedEndDate = endDate ? formatToCustomDate(endDate) : '';

  const displayValue =
    formattedStartDate && formattedEndDate
      ? `${formattedStartDate} - ${formattedEndDate}`
      : 'dd/MM/yyyy';

  return (
    <Box position="relative">
      <StyledDateRangePicker
        ref={datePickerRef}
        data-testid="date-range-picker"
        placeholder="YYYY-MM-DD HH:mm:ss"
        placement={placement}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={handleDateChange}
        defaultValue={[startDate, endDate]}
        format="yyyy-MM-dd HH:mm:ss"
        shouldDisableDate={disableDate}
        size="lg"
        cleanable={false}
      />

      <StyledTextField
        fullWidth
        variant="outlined"
        placeholder="dd/MM/yyyy"
        value={displayValue}
        onClick={handleTextFieldClick}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ margin: 'auto' }}>
              <CalendarTodayOutlined sx={{ fontSize: 21, color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <StyledArrow
                data-testid="date-picker-arrow"
                onClick={handleTextFieldClick}
              />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default CustomDateRangePicker;
